import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  ScrollView,
  FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getArticleSuggestions, getArticles } from '../services/articlesService';
import { addItemToList } from '../services/shoppingService';

const AddItemScreen = ({ route, navigation }) => {
  const { storeId, storeName } = route.params;
  const [searchQuery, setSearchQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [articlesResult, suggestionsResult] = await Promise.all([
      getArticles(),
      getArticleSuggestions(10),
    ]);

    if (articlesResult.success) {
      setArticles(articlesResult.data);
    }

    if (suggestionsResult.success) {
      setSuggestions(suggestionsResult.data);
    }

    setLoading(false);
  };

  const filteredArticles = searchQuery.trim()
    ? articles.filter(article =>
        article.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : suggestions;

  const handleSelectArticle = (article) => {
    setSelectedArticle(article);
    setQuantity(article.default_quantity?.toString() || '1');
    setUnit(article.default_unit || '');
    setSearchQuery('');
  };

  const handleAddToList = async () => {
    if (!selectedArticle) {
      Alert.alert('Fehler', 'Bitte wähle einen Artikel aus');
      return;
    }

    const quantityNum = parseFloat(quantity);
    if (isNaN(quantityNum) || quantityNum <= 0) {
      Alert.alert('Fehler', 'Bitte gib eine gültige Menge ein');
      return;
    }

    setAdding(true);
    const result = await addItemToList(storeId, {
      article_id: selectedArticle.id,
      quantity: quantityNum,
      unit: unit.trim() || selectedArticle.default_unit,
      notes: notes.trim() || null,
      tags: selectedArticle.default_tags || [],
    });

    setAdding(false);

    if (result.success) {
      navigation.goBack();
    } else {
      Alert.alert('Fehler', result.error);
    }
  };

  const renderArticleItem = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.articleItem,
        selectedArticle?.id === item.id && styles.articleItemSelected,
      ]}
      onPress={() => handleSelectArticle(item)}
    >
      <View style={styles.articleItemContent}>
        <Text style={styles.articleName}>{item.name}</Text>
        <Text style={styles.articleDetails}>
          {item.default_quantity} {item.default_unit}
          {item.purchase_count > 0 && ` • ${item.purchase_count}x gekauft`}
        </Text>
        {item.notes && (
          <Text style={styles.articleNotes} numberOfLines={1}>
            {item.notes}
          </Text>
        )}
      </View>
      {selectedArticle?.id === item.id && (
        <Ionicons name="checkmark-circle" size={24} color="#2E7D32" />
      )}
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color="#2E7D32" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.searchContainer}>
        <Ionicons name="search" size={20} color="#999" style={styles.searchIcon} />
        <TextInput
          style={styles.searchInput}
          placeholder="Artikel suchen..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <Ionicons name="close-circle" size={20} color="#999" />
          </TouchableOpacity>
        )}
      </View>

      {selectedArticle ? (
        <ScrollView style={styles.formContainer}>
          <View style={styles.selectedCard}>
            <Text style={styles.selectedLabel}>Ausgewählter Artikel</Text>
            <Text style={styles.selectedName}>{selectedArticle.name}</Text>
            <TouchableOpacity
              style={styles.changeButton}
              onPress={() => setSelectedArticle(null)}
            >
              <Text style={styles.changeButtonText}>Ändern</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Menge</Text>
            <TextInput
              style={styles.input}
              value={quantity}
              onChangeText={(text) => {
                // Erlaube beliebigen Text für Menge (z.B. "2-3", "ca. 500g", etc.)
                setQuantity(text);
              }}
              placeholder="z.B. 2, 2-3, ca. 500"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Einheit</Text>
            <TextInput
              style={styles.input}
              value={unit}
              onChangeText={setUnit}
              placeholder="z.B. Liter, Stück, kg"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Notizen (optional)</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={notes}
              onChangeText={setNotes}
              placeholder="z.B. Nur Bio, Auf Angebot achten"
              multiline
              numberOfLines={3}
            />
          </View>

          <TouchableOpacity
            style={styles.addButton}
            onPress={handleAddToList}
            disabled={adding}
          >
            {adding ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <Ionicons name="add-circle-outline" size={20} color="#fff" />
                <Text style={styles.addButtonText}>Zur Liste hinzufügen</Text>
              </>
            )}
          </TouchableOpacity>
        </ScrollView>
      ) : (
        <View style={styles.listContainer}>
          {!searchQuery && (
            <Text style={styles.sectionTitle}>
              {suggestions.length > 0 ? 'Häufig gekauft' : 'Alle Artikel'}
            </Text>
          )}
          <FlatList
            data={filteredArticles}
            renderItem={renderArticleItem}
            keyExtractor={item => item.id}
            contentContainerStyle={styles.listContent}
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Ionicons name="search-outline" size={48} color="#ccc" />
                <Text style={styles.emptyText}>
                  {searchQuery
                    ? 'Keine Artikel gefunden'
                    : 'Noch keine Artikel vorhanden'}
                </Text>
                <TouchableOpacity
                  style={styles.emptyButton}
                  onPress={() => navigation.navigate('Artikel')}
                >
                  <Text style={styles.emptyButtonText}>
                    Artikel erstellen
                  </Text>
                </TouchableOpacity>
              </View>
            }
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  searchIcon: {
    marginRight: 10,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    outline: 'none',
    borderWidth: 0,
  },
  listContainer: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    paddingHorizontal: 15,
    paddingTop: 15,
    paddingBottom: 10,
    textTransform: 'uppercase',
  },
  listContent: {
    paddingHorizontal: 15,
    paddingBottom: 15,
  },
  articleItem: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 15,
    marginBottom: 10,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  articleItemSelected: {
    borderColor: '#2E7D32',
    backgroundColor: '#E8F5E9',
  },
  articleItemContent: {
    flex: 1,
  },
  articleName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  articleDetails: {
    fontSize: 14,
    color: '#666',
  },
  articleNotes: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
    fontStyle: 'italic',
  },
  formContainer: {
    flex: 1,
    padding: 15,
  },
  selectedCard: {
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
  },
  selectedLabel: {
    fontSize: 12,
    color: '#2E7D32',
    fontWeight: '600',
    marginBottom: 5,
  },
  selectedName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  changeButton: {
    marginTop: 10,
    alignSelf: 'flex-start',
  },
  changeButtonText: {
    color: '#2E7D32',
    fontSize: 14,
    fontWeight: '600',
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#fff',
    padding: 15,
    borderRadius: 8,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  addButton: {
    backgroundColor: '#2E7D32',
    padding: 15,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  addButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: 40,
  },
  emptyText: {
    fontSize: 16,
    color: '#999',
    marginTop: 15,
  },
  emptyButton: {
    backgroundColor: '#2E7D32',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    marginTop: 15,
  },
  emptyButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
});

export default AddItemScreen;

